/**
 * TanStack Query factory functions for patients.
 *
 * Backed by the `patient_summary_list` and `patient_orders_history` RPCs
 * (Phase 4b) which join derived columns (Patient ID short_code, last order,
 * total orders) without leaking RLS scope.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@insolech/db';

export const patientsKeys = {
  all: ['patients'] as const,
  lists: () => [...patientsKeys.all, 'list'] as const,
  list: (params: PatientListParams) => [...patientsKeys.lists(), params] as const,
  detail: (patientId: string) => [...patientsKeys.all, 'detail', patientId] as const,
  history: (patientId: string) => [...patientsKeys.all, 'history', patientId] as const,
  lineHistory: (patientId: string) =>
    [...patientsKeys.all, 'lineHistory', patientId] as const,
  matches: (clinicId: string | null) =>
    [...patientsKeys.all, 'matches', clinicId ?? 'all'] as const,
};

export interface PatientListParams {
  clinicId?: string | null;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface PatientSummaryRow {
  id: string;
  full_name: string;
  /** Human-friendly patient code (e.g. P-26-00001). */
  patient_code: string | null;
  date_of_birth: string | null;
  gender: string | null;
  phone: string | null;
  email: string | null;
  language_preference: string;
  clinic_id: string;
  patient_id_short: string | null;
  last_order_date: string | null;
  total_orders: number;
}

export interface PatientListResult {
  rows: PatientSummaryRow[];
  total: number;
  page: number;
  pageSize: number;
}

export function patientsListQuery(client: SupabaseClient<Database>, params: PatientListParams) {
  return {
    queryKey: patientsKeys.list(params),
    queryFn: async (): Promise<PatientListResult> => {
      // R28 — cap raised to 5000 so the patients page can fetch the full
      // clinic dataset and filter / sort / paginate client-side from the
      // column-filter popovers (same pattern as ordersListQuery).
      const pageSize = Math.max(1, Math.min(params.pageSize ?? 25, 5000));
      const page = Math.max(0, params.page ?? 0);
      const args = {
        p_clinic_id: params.clinicId ?? null,
        p_search: params.search ?? null,
        p_limit: pageSize,
        p_offset: page * pageSize,
      };
      const { data, error } = await client.rpc(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'patient_summary_list' as any,
        args as unknown as Record<string, unknown>,
      );
      if (error) throw error;
      const rows = (data ?? []) as Array<Record<string, unknown> & { total_count?: number }>;
      const total = rows[0]?.total_count != null ? Number(rows[0].total_count) : rows.length;
      return {
        rows: rows.map((r) => ({
          id: String(r.id),
          full_name: String(r.full_name ?? ''),
          patient_code: (r.patient_code ?? null) as string | null,
          date_of_birth: (r.date_of_birth ?? null) as string | null,
          gender: (r.gender ?? null) as string | null,
          phone: (r.phone ?? null) as string | null,
          email: (r.email ?? null) as string | null,
          language_preference: String(r.language_preference ?? 'en'),
          clinic_id: String(r.clinic_id),
          patient_id_short: (r.patient_id_short ?? null) as string | null,
          last_order_date: (r.last_order_date ?? null) as string | null,
          total_orders: Number(r.total_orders ?? 0),
        })),
        total,
        page,
        pageSize,
      };
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  Patient detail (header + measurements + history)                          */
/* -------------------------------------------------------------------------- */

export interface PatientDetail {
  id: string;
  clinic_id: string;
  full_name: string;
  patient_code: string | null;
  date_of_birth: string | null;
  gender: string | null;
  phone: string | null;
  email: string | null;
  emergency_contact_name: string | null;
  emergency_contact_phone: string | null;
  notes: string | null;
  language_preference: string;
  is_archived: boolean;
  /** Direct-billing patients are creator-only (migration 20270221000000). */
  is_private: boolean;
  /** NULL on rows predating the column's DEFAULT auth.uid() - 16 of 72 live
   *  rows at the time of writing - which is why promoting such a row to private
   *  would hide it from everyone but an admin. */
  created_by: string | null;
  created_at: string;
}

export function patientDetailQuery(client: SupabaseClient<Database>, patientId: string) {
  return {
    queryKey: patientsKeys.detail(patientId),
    queryFn: async (): Promise<PatientDetail | null> => {
      const { data, error } = await client
        .from('patients')
        .select('*')
        .eq('id', patientId)
        .maybeSingle();
      if (error) throw error;
      if (!data) return null;
      const row = data as unknown as Record<string, unknown>;
      return {
        id: String(row.id),
        clinic_id: String(row.clinic_id),
        full_name: String(row.full_name ?? ''),
        patient_code: (row.patient_code ?? null) as string | null,
        date_of_birth: (row.date_of_birth ?? null) as string | null,
        gender: (row.gender ?? null) as string | null,
        phone: (row.phone ?? null) as string | null,
        email: (row.email ?? null) as string | null,
        emergency_contact_name: (row.emergency_contact_name ?? null) as string | null,
        emergency_contact_phone: (row.emergency_contact_phone ?? null) as string | null,
        notes: (row.notes ?? null) as string | null,
        language_preference: String(row.language_preference ?? 'en'),
        is_archived: Boolean(row.is_archived),
        // Needed by the wizard to decide whether promoting a record to private
        // is SAFE: a private row is visible only to its creator, so promoting a
        // row owned by someone else (or by nobody) hides it from people who can
        // see it today, with no un-private affordance anywhere in the app.
        is_private: Boolean(row.is_private),
        created_by: (row.created_by ?? null) as string | null,
        created_at: String(row.created_at),
      };
    },
  };
}

export interface PatientOrderHistoryRow {
  order_id: string;
  order_number: string;
  status: string;
  created_at: string;
  expected_delivery_date: string | null;
  actual_delivery_date: string | null;
  total_aed: number;
  patient_id_short: string | null;
  primary_line_type: string | null;
  primary_product_name: string | null;
  /** Subtype (pa12_insole, eva_insole, ...) of the first line, if any. */
  primary_solution_subtype: string | null;
  /** Configuration jsonb of the first line, used by formatProductLabel. */
  primary_configuration: Record<string, unknown> | null;
  clinic_id: string | null;
  clinic_name: string | null;
}

export function patientHistoryQuery(client: SupabaseClient<Database>, patientId: string) {
  return {
    queryKey: patientsKeys.history(patientId),
    queryFn: async (): Promise<PatientOrderHistoryRow[]> => {
      const { data, error } = await client.rpc(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'patient_orders_history' as any,
        { p_patient_id: patientId } as unknown as Record<string, unknown>,
      );
      if (error) throw error;
      const rows = (data ?? []) as Array<Record<string, unknown>>;
      return rows.map((r) => ({
        order_id: String(r.order_id),
        order_number: String(r.order_number ?? ''),
        status: String(r.status ?? 'draft'),
        created_at: String(r.created_at ?? ''),
        expected_delivery_date: (r.expected_delivery_date ?? null) as string | null,
        actual_delivery_date: (r.actual_delivery_date ?? null) as string | null,
        total_aed: Number(r.total_aed ?? 0),
        patient_id_short: (r.patient_id_short ?? null) as string | null,
        primary_line_type: (r.primary_line_type ?? null) as string | null,
        primary_product_name: (r.primary_product_name ?? null) as string | null,
        primary_solution_subtype: (r.primary_solution_subtype ?? null) as string | null,
        primary_configuration: (r.primary_configuration ?? null) as
          | Record<string, unknown>
          | null,
        clinic_id: (r.clinic_id ?? null) as string | null,
        clinic_name: (r.clinic_name ?? null) as string | null,
      }));
    },
  };
}

/* -------------------------------------------------------------------------- */
/*  Patient mutations                                                         */
/* -------------------------------------------------------------------------- */

/**
 * One PAST LINE that a new prescription could be copied from.
 *
 * Distinct from PatientOrderHistoryRow, which collapses each order to its
 * first line (`limit 1` on every subquery in patient_orders_history). That was
 * fine when an order was one device, but reorder carts have been normal since
 * PR #85, and 44 lines in production are currently invisible to the
 * header-keyed function. Choosing a prescription to reuse is a per-LINE
 * decision, so the list has to be per line.
 *
 * line_type / solution_subtype / material are exactly the "product type,
 * subtype and material" a podiatrist needs to judge whether a past
 * prescription is a sensible starting point for the one they are writing.
 */
export interface PatientOrderLineHistoryRow {
  line_id: string;
  order_id: string;
  order_number: string;
  status: string;
  created_at: string;
  line_number: number;
  line_type: string | null;
  solution_subtype: string | null;
  product_name: string | null;
  configuration: Record<string, unknown> | null;
  patient_id_short: string | null;
  clinic_id: string | null;
  clinic_name: string | null;
}

/** Shell material as stored on the line, for display next to the subtype. */
export function lineMaterial(row: PatientOrderLineHistoryRow): string | null {
  const raw = row.configuration?.['material'];
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

export function patientLineHistoryQuery(
  client: SupabaseClient<Database>,
  patientId: string,
) {
  return {
    queryKey: patientsKeys.lineHistory(patientId),
    queryFn: async (): Promise<PatientOrderLineHistoryRow[]> => {
      const { data, error } = await client.rpc(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'patient_order_lines_history' as any,
        { p_patient_id: patientId } as unknown as Record<string, unknown>,
      );
      if (error) throw error;
      const rows = (data ?? []) as Array<Record<string, unknown>>;
      return rows.map((r) => ({
        line_id: String(r.line_id),
        order_id: String(r.order_id),
        order_number: String(r.order_number ?? ''),
        status: String(r.status ?? 'draft'),
        created_at: String(r.created_at ?? ''),
        line_number: Number(r.line_number ?? 1),
        line_type: (r.line_type ?? null) as string | null,
        solution_subtype: (r.solution_subtype ?? null) as string | null,
        product_name: (r.product_name ?? null) as string | null,
        configuration: (r.configuration ?? null) as Record<string, unknown> | null,
        patient_id_short: (r.patient_id_short ?? null) as string | null,
        clinic_id: (r.clinic_id ?? null) as string | null,
        clinic_name: (r.clinic_name ?? null) as string | null,
      }));
    },
  };
}

export interface PatientCreateInput {
  clinic_id: string;
  /** Optional — when provided alongside last_name, full_name is derived. */
  full_name?: string;
  /** Phase 4b iteration: split into first_name + last_name. Either may be supplied. */
  first_name?: string;
  last_name?: string;
  date_of_birth?: string | null | undefined;
  gender?: string | null | undefined;
  phone?: string | null | undefined;
  email?: string | null | undefined;
  language_preference?: 'en' | 'ar' | 'es' | undefined;
  /** Free-text notes / observations about the patient. */
  notes?: string | null | undefined;
  /**
   * When true, marks patients.is_private = true so the patient row is
   * visible only to the podiatrist who created it (RLS rewrite is a
   * follow-up migration). Set by the wizard when the order is being
   * direct-billed. Defaults to false.
   */
  is_private?: boolean | undefined;
}

/**
 * Create a patient row.
 *
 * Phase 4b iteration: the canonical source of truth on `patients` is now
 * `first_name` + `last_name`; `full_name` is a generated column. Callers
 * SHOULD pass first_name + last_name. Legacy callers passing `full_name`
 * are split heuristically at the first whitespace.
 */
export async function createPatient(
  client: SupabaseClient<Database>,
  input: PatientCreateInput,
): Promise<{ id: string; full_name: string; patient_code: string | null }> {
  let firstName = input.first_name?.trim() ?? '';
  let lastName = input.last_name?.trim() ?? '';
  if ((!firstName || !lastName) && input.full_name) {
    const parts = input.full_name.trim().split(/\s+/);
    if (!firstName) firstName = parts[0] ?? '';
    if (!lastName) lastName = parts.slice(1).join(' ') || '—';
  }

  // We cannot insert into the generated `full_name` column. Strip it.
  const payload: Record<string, unknown> = {
    clinic_id: input.clinic_id,
    first_name: firstName,
    last_name: lastName,
    date_of_birth: input.date_of_birth ?? null,
    gender: input.gender ?? null,
    phone: input.phone ?? null,
    email: input.email ?? null,
    language_preference: input.language_preference ?? 'en',
  };
  if (input.notes != null) payload.notes = input.notes;
  if (input.is_private != null) payload.is_private = input.is_private;
  const { data, error } = await client
    .from('patients')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .insert(payload as any)
    .select('id, full_name, patient_code')
    .single();
  if (error) throw error;
  return data as { id: string; full_name: string; patient_code: string | null };
}

export async function updatePatient(
  client: SupabaseClient<Database>,
  patientId: string,
  patch: Partial<PatientCreateInput> & {
    notes?: string | null;
    emergency_contact_name?: string | null;
    emergency_contact_phone?: string | null;
  },
): Promise<void> {
  // Strip `full_name` if present — it's a generated column post-iteration.
  const { full_name: _stripped, ...rest } = patch as Record<string, unknown> & {
    full_name?: string;
  };
  void _stripped;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await client.from('patients').update(rest as any).eq('id', patientId);
  if (error) throw error;
}

/* -------------------------------------------------------------------------- */
/*  Duplicate-patient match candidates                                        */
/* -------------------------------------------------------------------------- */

/**
 * A single pair of patients within the same clinic that the backend believes
 * MAY be the same person (matching first_name + last_name plus at least one
 * of phone/email). Returned by patient_match_candidates (Task B), consumed by
 * the PatientsList "Review duplicates" banner.
 */
export interface MatchCandidatePatient {
  id: string;
  full_name: string;
  patient_code: string | null;
  phone: string | null;
  email: string | null;
  total_orders: number;
  last_order_date: string | null;
  created_at: string | null;
}

export interface MatchCandidate {
  /** Stable pair key, used as the React key for each card. */
  pair_id: string;
  match_strength: 'strong' | 'moderate';
  /** Patient A. By convention the side with more orders (then older created_at). */
  a: MatchCandidatePatient;
  /** Patient B, the proposed "merge into A" duplicate. */
  b: MatchCandidatePatient;
}

export function patientMatchCandidatesQuery(
  client: SupabaseClient<Database>,
  clinicId: string | null,
) {
  return {
    queryKey: patientsKeys.matches(clinicId),
    queryFn: async (): Promise<MatchCandidate[]> => {
      const { data, error } = await client.rpc(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        'patient_match_candidates' as any,
        { p_clinic_id: clinicId } as unknown as Record<string, unknown>,
      );
      if (error) throw error;
      return ((data ?? []) as unknown as MatchCandidate[]) ?? [];
    },
  };
}

/**
 * Merge `sourcePatientId` INTO `targetPatientId`. The source row's orders and
 * QR tokens are re-pointed at the target; the source is soft-deleted.
 * Server-side RPC enforces same-clinic + caller-is-member.
 */
export async function mergePatients(
  client: SupabaseClient<Database>,
  args: { targetPatientId: string; sourcePatientId: string },
): Promise<void> {
  const { error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'merge_patients' as any,
    {
      // KNOWN BROKEN, tracked as housekeeping backlog item 13. These names do
      // not match the live function (p_keep_id / p_drop_id), and PostgREST
      // resolves an RPC by its named arguments, so this 404s at runtime rather
      // than failing to compile. Fixed deliberately in its own change, with
      // the duplicate-record merge tool, because merge is destructive and
      // getting keep/drop the wrong way round deletes the wrong patient.
      p_target_id: args.targetPatientId,
      p_source_id: args.sourcePatientId,
    } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
}

/**
 * Record that the operator reviewed this pair and decided the two patients
 * are NOT the same person, so the pair should not appear in future match
 * suggestions.
 */
export async function markPatientsDistinct(
  client: SupabaseClient<Database>,
  args: { patientAId: string; patientBId: string },
): Promise<void> {
  const { error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'mark_patients_distinct' as any,
    {
      // Same defect as mergePatients above, same backlog item 13: the live
      // signature is p_a_id / p_b_id, so mark-as-distinct 404s too.
      p_patient_a_id: args.patientAId,
      p_patient_b_id: args.patientBId,
    } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
}

/**
 * Search patients by full_name (used by the new-order wizard's patient picker).
 */
export async function searchPatients(
  client: SupabaseClient<Database>,
  args: { clinicId: string | null; search: string; limit?: number },
): Promise<PatientSummaryRow[]> {
  const { data, error } = await client.rpc(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    'patient_summary_list' as any,
    {
      p_clinic_id: args.clinicId,
      p_search: args.search,
      p_limit: args.limit ?? 10,
      p_offset: 0,
    } as unknown as Record<string, unknown>,
  );
  if (error) throw error;
  const rows = (data ?? []) as Array<Record<string, unknown>>;
  return rows.map((r) => ({
    id: String(r.id),
    full_name: String(r.full_name ?? ''),
    patient_code: (r.patient_code ?? null) as string | null,
    date_of_birth: (r.date_of_birth ?? null) as string | null,
    gender: (r.gender ?? null) as string | null,
    phone: (r.phone ?? null) as string | null,
    email: (r.email ?? null) as string | null,
    language_preference: String(r.language_preference ?? 'en'),
    clinic_id: String(r.clinic_id),
    patient_id_short: (r.patient_id_short ?? null) as string | null,
    last_order_date: (r.last_order_date ?? null) as string | null,
    total_orders: Number(r.total_orders ?? 0),
  }));
}
